/* mv6-calm-rest.js — the v6 PDP's UI layer.
 *
 * Renders text and flips attributes. It owns NO pricing arithmetic: every
 * figure it prints comes from `mv6.state()` (assets/mv6-purchase.js), and
 * every cart mutation goes through the module's API. If you find yourself
 * about to write a `*` or a `-` on a price in this file, the number belongs
 * in mv6-purchase.js instead.
 *
 * Selected / hidden states are CSS, driven by four attributes on the root:
 *   data-mv6-mode="sub|ot"   which offer card is chosen (and its copy)
 *   data-mv6-qty="1|2|3"     which quantity tile is chosen
 *   data-mv6-nocompare="0|1" a single one-time bottle pays list: no
 *                            strikethrough, no % badge, no Save line
 *   data-mv6-cart="empty|filled"
 */
(function () {
  'use strict';

  var TAG = document.currentScript;
  var ROOT = document.getElementById(TAG.getAttribute('data-root'));
  if (!ROOT) { console.error('[mv6] section root not found'); return; }
  if (!window.mv6) { console.error('[mv6] mv6-purchase.js did not load — the buy box cannot price itself.'); return; }

  var mv6 = window.mv6;
  var CUTOFF_HOUR = parseInt(TAG.getAttribute('data-cutoff-hour'), 10);
  if (isNaN(CUTOFF_HOUR)) CUTOFF_HOUR = 14;
  var CAPSULES = parseInt(TAG.getAttribute('data-capsules'), 10) || 60;
  var SERVINGS = parseInt(TAG.getAttribute('data-servings'), 10) || 30;

  /* ── tiny DOM helpers ─────────────────────────────────────────────────── */
  function $(sel) { return ROOT.querySelector(sel); }
  function $$(sel) { return Array.prototype.slice.call(ROOT.querySelectorAll(sel)); }
  function set(key, text) {
    var str = String(text);
    $$('[data-mv6="' + key + '"]').forEach(function (el) { if (el.textContent !== str) el.textContent = str; });
  }
  function show(el, on) { if (el) { if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', ''); } }
  function showAll(sel, on) { $$(sel).forEach(function (el) { show(el, on); }); }
  function attr(name, val) { if (ROOT.getAttribute(name) !== String(val)) ROOT.setAttribute(name, val); }

  var money = mv6.money;

  /* ── the moving fade, round 3 (23 Aug 2026, user calls, Furness reference) ─
   * Two pieces, both opacity-only — the NUMBERS THEMSELVES NEVER MOVE (the
   * digit-rise of round 2 read as "jumping"):
   * · THE SWEEP — starts the INSTANT a cart write is queued (busy rising
   *   edge): a subtle dim travels through the current figure from the dollar
   *   sign left→right and loops while the request is in flight — the
   *   "loading" read the user asked for. Stops on settle.
   * · THE ARRIVAL — a changed figure fades in per-character, staggered
   *   left→right from the dollar sign. No translate, no count-up (both
   *   tried, both rejected on sight).
   * Char spans keep textContent identical to the plain string, so set()'s
   * change-detection and screen readers are unaffected. First paint and
   * closed-drawer updates land instantly. */
  var fadeVals = {};
  function splitChars(el, str) {
    el.textContent = '';
    var out = [];
    for (var i = 0; i < str.length; i++) {
      var c = document.createElement('span');
      c.textContent = str.charAt(i);
      c.style.display = 'inline-block';
      c.style.whiteSpace = 'pre';
      el.appendChild(c);
      out.push(c);
    }
    return out;
  }
  function setFade(key, text) {
    var str = String(text);
    var prev = fadeVals[key];
    fadeVals[key] = str;
    var els = $$('[data-mv6="' + key + '"]');
    if (prev === str || prev == null || !cartOpen) {
      els.forEach(function (el) { if (el.textContent !== str) el.textContent = str; });
      return;
    }
    sweepStop(key);   /* the loading sweep hands off to the arrival */
    els.forEach(function (el) {
      splitChars(el, str).forEach(function (c, i) {
        /* fill:'backwards' is load-bearing: a delayed char must START hidden */
        if (c.animate) c.animate(
          [{ opacity: 0.1 }, { opacity: 1 }],
          { duration: 300, delay: i * 45, easing: 'ease-out', fill: 'backwards' }
        );
      });
    });
  }
  /* the in-flight sweep — keyed on the SAME busy signal as the pending ring */
  var MONEY_KEYS = ['subPrice', 'otPrice', 'cartTotal', 'sumCompare', 'sumDiscount'];
  var sweeps = {}, sweepOn = false;
  function sweepStop(key) {
    (sweeps[key] || []).forEach(function (a) { try { a.cancel(); } catch (e) {} });
    sweeps[key] = null;
  }
  function sweepStart(key) {
    if (sweeps[key]) return;
    var list = [];
    $$('[data-mv6="' + key + '"]').forEach(function (el) {
      if (el.hasAttribute('hidden') || !el.textContent) return;
      splitChars(el, el.textContent).forEach(function (c, i) {
        if (c.animate) list.push(c.animate(
          [{ opacity: 1 }, { opacity: 0.35 }, { opacity: 1 }],
          { duration: 1000, delay: i * 55, iterations: Infinity, easing: 'ease-in-out' }
        ));
      });
    });
    sweeps[key] = list;
  }
  function sweepSync(busy) {
    if (busy && cartOpen && !sweepOn) { sweepOn = true; MONEY_KEYS.forEach(sweepStart); }
    else if (!busy && sweepOn) { sweepOn = false; MONEY_KEYS.forEach(sweepStop); }
  }
  function negMoney(c) { return '-' + money(c); }

  /* ── optimistic quantity — the tapped value OWNS the stepper display until
   * every queued step for that line settles. Without this the queue's own
   * fresh-cart read repainted the OLD quantity mid-flight and the number
   * flip-flopped 3→2→3 on the live drawer (user report, 22 Aug 2026). The
   * price deliberately does NOT move optimistically — it holds, then fades to
   * the settled figure (money is never guessed, R-101 family). */
  var optQty = { sub: null, ot: null }, optPend = { sub: 0, ot: 0 };
  function qtyShown(kind, line) {
    return optPend[kind] > 0 && optQty[kind] != null ? optQty[kind] : line.qty;
  }
  function stepOptimistic(kind, line, delta) {
    var next = qtyShown(kind, line) + delta;
    if (next > line.cap || next < 0) return;
    optPend[kind] += 1;
    sweepSync(true);   /* the loading sweep starts ON THE TAP, not on the next render frame */
    if (next >= 1) { optQty[kind] = next; set(kind + 'Qty', next); }   /* qty snaps plain — no animation (user call, 23 Aug) */
    function settle() {
      optPend[kind] -= 1;
      /* repaint from FRESH truth, never from `last`: the last RENDERED state
         can predate the settle (rAF-coalesced), and painting it flashed the
         old number for a frame before the final render corrected it — the
         double-blick (user report, 23 Aug 2026) */
      if (optPend[kind] <= 0) { optPend[kind] = 0; optQty[kind] = null; renderCart(mv6.state()); }
    }
    mv6.step(kind, delta).then(settle, settle);
  }

  /* ── R-103 · an error must reach the PAGE, not the console ─────────────
   * A /cart/add.js failure — sold out mid-session, a Shopify 422, an app that
   * hijacks fetch — used to be a `console.warn` and nothing else: the pending
   * spinner cleared, the button looked idle again, and nothing had been added.
   * The customer's only signal was the absence of one. Every [data-mv6-error]
   * slot paints `state().error`; role="alert" carries it to a screen reader.
   * It also carries the permanent unsellable state (R-101), which is why the
   * buy box has a slot of its own rather than only the drawer. */
  function paintError(msg) {
    var str = msg || '';
    $$('[data-mv6-error]').forEach(function (el) {
      if (el.textContent !== str) el.textContent = str;
      el.style.display = str ? 'block' : 'none';
    });
  }

  /* When the money path is closed (R-101) the ADD controls say so. Checkout is
     deliberately NOT disabled: the cart may already hold valid lines, and a
     customer must always be able to finish paying for them. */
  var buyLocked = null;
  function lockBuying(on) {
    if (buyLocked === on) return;
    buyLocked = on;
    $$('[data-mv6-cta],[data-mv6-empty-add],[data-mv6-upgrade]').forEach(function (b) {
      if (on) {
        b.setAttribute('disabled', ''); b.setAttribute('aria-disabled', 'true');
        b.style.opacity = '.5'; b.style.cursor = 'not-allowed';
      } else {
        b.removeAttribute('disabled'); b.removeAttribute('aria-disabled');
        /* 'pointer', not '' — the clear WIPES the tag's own inline cursor:pointer
           (style.cursor='' deletes the property), so every unlock left the CTA,
           empty-add and upgrade buttons showing the arrow (user report, 23 Aug) */
        b.style.opacity = ''; b.style.cursor = 'pointer';
      }
    });
  }

  /* ── ship date ────────────────────────────────────────────────────────────
   * Amazon MCF ships seven days a week, so it is only ever today or tomorrow.
   * Computed client-side against the shopper's clock, as in the design. */
  var SHIP_DAY = (function () {
    var d = new Date();
    var today = new Date();
    if (d.getHours() >= CUTOFF_HOUR) d.setDate(d.getDate() + 1);
    var label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return d.toDateString() === today.toDateString() ? 'today, ' + label : label;
  })();

  /* ── the cart nudge chips ─────────────────────────────────────────────────
   * Colour tracks CLICKABILITY, not quantity: violet + ⊕ + sheen means there
   * is an offer left to claim, green + tag means it is banked. Green on an
   * unclaimed offer read as "already applied" and nobody pressed it.
   * The subscribe chip quotes MARGINAL points ("5% more") because the plan
   * already carries 30; the one-time chip quotes the total off list (15/20),
   * matching the Shopify discount names. Never mix the two readings — that is
   * the percentage-of-a-percentage trap this product has hit four times. */
  function nudge(isSub, qty) {
    if (isSub) {
      if (qty >= 3) return { text: '40% off — best price', act: false };
      return { text: qty === 2 ? 'Add a 3rd, save 5% more' : 'Add a 2nd, save 5% more', act: true };
    }
    if (qty >= 3) return { text: '20% off — banked', act: false };
    return { text: qty === 2 ? 'Add a 3rd, save 20%' : 'Add a 2nd, save 15%', act: true };
  }

  function paintNudge(which, line) {
    var btn = $('[data-mv6-nudge="' + which + '"]');
    if (!btn || !line) return;
    var n = nudge(line.isSub, line.qty);
    set(which + 'Nudge', n.text);
    btn.style.background = n.act ? '#EFE8F9' : '#F0F6EE';
    btn.style.color = n.act ? '#4A3A6E' : '#40693C';
    btn.style.borderColor = n.act ? '#D8CDE9' : 'transparent';
    btn.style.cursor = n.act ? 'pointer' : 'default';
    show(btn.querySelector('[data-mv6-nudge-plus]'), n.act);
    show(btn.querySelector('[data-mv6-nudge-tag]'), !n.act);
    show(btn.querySelector('[data-mv6-nudge-sheen]'), n.act);
    btn.dataset.act = n.act ? '1' : '0';
  }

  /* ── render ───────────────────────────────────────────────────────────── */
  var last = null;

  function render(s) {
    last = s;
    var q = s.qty;

    attr('data-mv6-mode', s.mode);
    attr('data-mv6-qty', Math.min(3, q));
    attr('data-mv6-nocompare', s.showCompare ? '0' : '1');

    /* price block */
    set('heroPrice', money(s.total));
    set('heroWas', money(s.compareTotal));
    set('heroPct', s.discountPct + '% OFF');
    set('heroSave', 'Save ' + money(s.savings));
    set('heroMeta', q === 1
      ? CAPSULES + ' capsules \u00a0\u00b7\u00a0 2 per night \u00a0\u00b7\u00a0 ' + SERVINGS + ' servings'
      : q + ' bottles \u00a0\u00b7\u00a0 ' + (CAPSULES * q) + ' capsules \u00a0\u00b7\u00a0 ' + (SERVINGS * q) + ' servings');

    /* the two offer cards — both always price the same quantity */
    var sc = s.cards.sub, oc = s.cards.ot;
    set('subCardPct', sc.pct + '% OFF');
    set('subCardTotal', money(sc.total));
    set('subCardWas', money(sc.compare));
    set('subCardPer', sc.per + ' per serving');
    set('otCardPct', oc.pct + '% OFF');
    set('otCardTotal', money(oc.total));
    set('otCardWas', money(oc.compare));
    set('otCardPer', oc.per + ' per serving');
    set('subUnitPrice', money(sc.unit));

    /* quantity tiles */
    s.tiers.forEach(function (t) {
      set('tile' + t.qty + 'Supply', t.servings + ' servings');
      set('tile' + t.qty + 'Per', t.per + '/serving');
    });

    /* CTA, price puck, sticky bar. One label both modes (user call, 28 Aug 2026):
       the CTA and the sticky bar say "Try Risk-Free" for sub AND one-time. */
    var label = 'Try Risk-Free';
    set('ctaLabel', label);
    set('ctaTotal', money(s.total));
    set('barCta', label);
    set('puckUnit', money(s.unit));
    set('puckPer', s.perServing);
    set('barUnit', money(s.unit));
    set('barWas', money(mv6.data.compare_base));
    set('barPct', s.discountPct + '% OFF');
    set('shipDay', SHIP_DAY);

    var cta = $('[data-mv6-cta]');
    if (cta) { cta.disabled = !!s.busy; cta.style.opacity = s.busy ? '.72' : '1'; }

    renderCart(s);
  }

  function renderCart(s) {
    var t = s.totals;
    var sub = s.subLine, ot = s.otLine;
    var filled = !!(sub || ot);
    sweepSync(!!s.busy);   /* the loading sweep starts the instant a write queues */

    attr('data-mv6-cart', filled ? 'filled' : 'empty');
    set('cartTitle', t.bottles > 0 ? 'Your cart (' + t.bottles + ')' : 'Your cart');

    var badge = $('[data-mv6-cart-count]');
    if (badge) { badge.textContent = t.itemCount || ''; show(badge, t.itemCount > 0); }

    /* empty-state cross-sell card: one bottle, subscribed. Read from the
       plan's own price, never a literal. */
    var tier1 = mv6.data.plans['1'];
    set('emptyWas', money(mv6.data.compare_base));
    set('emptyPrice', money((tier1 && tier1.price) || mv6.data.price));
    set('emptySupply', CAPSULES + ' caps · ' + SERVINGS + ' servings');

    /* free-shipping progress */
    set('shipMsg', t.freeShipping
      ? "Congrats! You've unlocked FREE SHIPPING!"
      : 'Add ' + money(t.freeShipLeft) + ' more to unlock free shipping');
    var msgWrap = $('[data-mv6-ship-msg-wrap]');
    if (msgWrap) msgWrap.style.color = t.freeShipping ? '#4C7A46' : '#4A3A6E';
    var bar = $('[data-mv6-ship-bar]');
    if (bar) {
      bar.style.width = t.freeShipPct + '%';
      bar.style.background = t.freeShipping ? 'linear-gradient(90deg,#8FC98A,#5E9E59)' : 'linear-gradient(90deg,#6E5E86,#4A3A6E)';
    }

    /* subscription line */
    show($('[data-mv6-line="sub"]'), !!sub);
    if (sub) {
      set('subQty', qtyShown('sub', sub));
      setFade('subPrice', money(sub.total));
      set('subCompare', money(sub.compare));
      set('subPct', sub.pct + '% OFF');
      set('subSave', 'Saving ' + money(sub.save) + ' every delivery');
      set('subSubtitle', sub.capsules + ' capsules · ' + sub.servings + ' servings');
      set('subCadenceLabel', 'Delivered every ' + (s.cadence || '4') + ' weeks');
      /* never fight the finger: skip the write while the select is being used */
      var cadSel = $('[data-mv6-cadence]');
      if (cadSel && cadSel.value !== (s.cadence || '4') && document.activeElement !== cadSel) cadSel.value = s.cadence || '4';
      showAll('[data-mv6-sub-disc]', sub.hasDisc);
      show($('[data-mv6-sub-max]'), sub.atCap);
      var inc = $('[data-mv6-step="sub+"]');
      if (inc) { inc.style.opacity = sub.atCap ? '.34' : '1'; inc.style.cursor = sub.atCap ? 'default' : 'pointer'; }
      paintNudge('sub', sub);
    }

    /* one-time line — deliberately uncapped: the fixed per-item discount
       continues, so qty 4 and 5 hold a steady 20% off. */
    show($('[data-mv6-line="ot"]'), !!ot);
    if (ot) {
      set('otQty', qtyShown('ot', ot));
      setFade('otPrice', money(ot.total));
      set('otCompare', money(ot.compare));
      set('otPct', ot.pct + '% OFF');
      set('otSave', 'Saving ' + money(ot.save));
      set('otSubtitle', ot.capsules + ' capsules · ' + ot.servings + ' servings · ships once');
      showAll('[data-mv6-ot-disc]', ot.hasDisc);
      paintNudge('ot', ot);
    }

    /* upgrade card — dollars, and only while the figure stays monotonic */
    show($('[data-mv6-upsell]'), s.upsell.show);
    if (s.upsell.show) set('upsellAmt', money(s.upsell.amount));

    /* summary + footer */
    setFade('sumCompare', money(t.compare));
    setFade('sumDiscount', negMoney(t.save));
    show($('[data-mv6-disc-row]'), t.hasSave);
    show($('[data-mv6-chip="sub"]'), !!(sub && sub.hasDisc));
    if (sub && sub.hasDisc) set('chipSub', 'Subscribe & save ' + sub.pct + '%');
    show($('[data-mv6-chip="ot"]'), !!(ot && ot.hasDisc));
    if (ot && ot.hasDisc) set('chipOt', 'One-time ' + ot.pct + '% off');
    /* Parity with the design's fourth chip. It reports the ADDITIVE tier bonus,
       which exists from two bottles up in either mode — the theme's line view has
       no qtyBonus field (that key is DC-side), and quantity is the same signal:
       one bottle earns no tier bonus, two and three do. */
    show($('[data-mv6-chip="qty"]'), !!((sub && sub.qty >= 2) || (ot && ot.qty >= 2)));
    show($('[data-mv6-chip="ship"]'), t.freeShipping);
    set('shipRow', t.freeShipping ? 'FREE SHIPPING' : 'Calculated at checkout');
    var shipRow = $('[data-mv6="shipRow"]');
    if (shipRow) shipRow.style.color = t.freeShipping ? '#4C7A46' : '#4A3A6E';

    set('cartCompare', money(t.compare));
    set('cartPct', t.pct + '% OFF');
    setFade('cartTotal', money(t.total));
    showAll('[data-mv6-cart-disc]', t.hasSave);

    paintError(s.error);
    lockBuying(s.sellable === false);
    if (s.error) console.warn('[mv6] cart error:', s.error);
  }

  /* ── drawer / menu open-close ─────────────────────────────────────────── */
  var cartOpen = false, menuOpen = false;

  /* cadence picker (22 Aug 2026): selects fire 'change', not 'click' — the one
     delegated listener the click path cannot cover. */
  ROOT.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.hasAttribute && t.hasAttribute('data-mv6-cadence')) { sweepSync(true); mv6.setCadence(t.value); }
  });

  function lock(on) {
    document.documentElement.classList.toggle('mv6-locked', on);
  }

  /* R-128 · bfcache: the checkout round-trip restores the page with the drawer open
     and the scroll lock held. Close it (setCart owns the lock discipline, R-26). */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && cartOpen) setCart(false);
  });

  var cartOffTimer = 0;
  var cartPrevFocus = null;

  function cartFocusables() {
    var panel = $('[data-mv6-cart-panel]');
    if (!panel) return [];
    return Array.prototype.filter.call(
      panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'),
      function (el) { return el.getClientRects().length > 0; }
    );
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !cartOpen) return;
    var panel = $('[data-mv6-cart-panel]');
    if (!panel) return;
    var f = cartFocusables();
    if (!f.length) return;
    var first = f[0], lastF = f[f.length - 1], a = document.activeElement;
    if (!panel.contains(a)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && a === first) { e.preventDefault(); lastF.focus(); }
    else if (!e.shiftKey && a === lastF) { e.preventDefault(); first.focus(); }
  });

  function setCart(open) {
    if (open && !cartOpen) cartPrevFocus = document.activeElement;
    cartOpen = open;
    var panel = $('[data-mv6-cart-panel]'), scrim = $('[data-mv6-cart-scrim]');
    /* R-75 / round 19: these two are display:none (`data-mv6-off`) whenever the
       drawer is shut — visibility alone did not stop iOS compositing the closed
       panel as a full-width light band over the footer (the user's
       ?mv6t=nooverlays A/B). Un-drop BEFORE the slide, with a forced reflow so
       the transition has a start value; re-drop only after it has finished. */
    clearTimeout(cartOffTimer);
    if (open) {
      panel.removeAttribute('data-mv6-off');
      scrim.removeAttribute('data-mv6-off');
      void panel.offsetWidth;
      /* Clarity perf round (22 Aug 2026): the thumbs ship loading="lazy" so a
         closed drawer costs zero bytes at load; strip it as the slide starts so
         the 400ms open animation covers the ~10 KB fetch (no pop-in, R-58). */
      Array.prototype.forEach.call(panel.querySelectorAll('img[loading="lazy"]'), function (im) { im.removeAttribute('loading'); });
    }
    panel.style.transform = open ? 'translateX(0)' : 'translateX(103%)';
    panel.style.pointerEvents = open ? 'auto' : 'none';
    scrim.style.opacity = open ? '1' : '0';
    scrim.style.pointerEvents = open ? 'auto' : 'none';
    if (open) { panel.setAttribute('data-mv6-on', ''); scrim.setAttribute('data-mv6-on', ''); }
    else {
      panel.removeAttribute('data-mv6-on'); scrim.removeAttribute('data-mv6-on');
      cartOffTimer = setTimeout(function () {
        if (cartOpen) return;
        panel.setAttribute('data-mv6-off', '');
        scrim.setAttribute('data-mv6-off', '');
      }, 460);
    }
    lock(open);
    if (open) { menu(false); barSync(); }
    /* R-104 · a dialog that never moves focus is a dialog for the mouse only.
       The panel declares role="dialog" aria-modal="true", so a keyboard or
       screen-reader user is told a modal opened and then left standing behind
       it. Focus goes to Close on open (the one control that always exists),
       Tab cycles inside the panel (the keydown listener below), Escape already
       closed it, and the trigger gets focus back on close — without which the
       keyboard user restarts from the top of the document.
       preventScroll matters: the page is scroll-locked at a pinned offset
       (R-52) and a focus-driven scroll would fight it. */
    if (open) {
      var closeBtn = panel.querySelector('[data-mv6-cart-close]');
      if (closeBtn) { try { closeBtn.focus({ preventScroll: true }); } catch (err) { closeBtn.focus(); } }
    } else if (cartPrevFocus && document.contains(cartPrevFocus)) {
      try { cartPrevFocus.focus({ preventScroll: true }); } catch (err) {}
      cartPrevFocus = null;
    }
    /* Tell the header section (outside this root) — it closes its menu and
       freezes hide-on-scroll while the drawer holds the scroll lock. */
    try { document.dispatchEvent(new CustomEvent('mv6:drawer', { detail: { open: open } })); } catch (e) {}
  }

  /* The header's cart button lives in sections/mv6-header.liquid (outside this
     root), so its clicks can't reach the ROOT listener below — it dispatches
     this event instead. ONE drawer mechanism either way (R-36). */
  document.addEventListener('mv6:cart-open', function () { setCart(true); });

  function menu(open) {
    menuOpen = open;
    var drop = $('[data-mv6-menu]'), scrim = $('[data-mv6-menu-scrim]'), nav = $('[data-mv6-nav]');
    /* Chrome extraction (14 Aug 2026): the menu lives in sections/mv6-header.liquid
       now, OUTSIDE this root — mv6-header.js owns it. This stays only for a page
       that still embeds a menu; with none present it is a no-op, never a crash
       (setCart calls menu(false) unconditionally). */
    if (!drop || !scrim) return;
    drop.style.opacity = open ? '1' : '0';
    drop.style.pointerEvents = open ? 'auto' : 'none';
    drop.style.transform = open ? 'scale(1) translateY(0)' : 'scale(.94) translateY(-8px)';
    scrim.style.opacity = open ? '1' : '0';
    scrim.style.pointerEvents = open ? 'auto' : 'none';
    /* The nav's z-index is deliberately NOT touched here. Changing it on an
       element that carries backdrop-filter forces the browser to re-rasterise
       the blurred layer, and that was the header flicker on menu open/close.
       The panel and its scrim sit below the nav's own 60 and above the page,
       so the ladder already works without moving the header. */
    $$('[data-mv6-burger="1"]').forEach(function (el) { el.style.transform = open ? 'translateY(2.9px) rotate(45deg)' : 'none'; });
    $$('[data-mv6-burger="2"]').forEach(function (el) { el.style.transform = open ? 'translateY(-2.9px) rotate(-45deg)' : 'none'; });
  }

  /* ── events ───────────────────────────────────────────────────────────── */
  ROOT.addEventListener('click', function (e) {
    var el = e.target.closest ? e.target.closest('[data-mv6-pick],[data-mv6-tile],[data-mv6-cta],[data-mv6-cart-open],[data-mv6-cart-close],[data-mv6-cart-scrim],[data-mv6-menu-toggle],[data-mv6-menu-scrim],[data-mv6-step],[data-mv6-nudge],[data-mv6-line-remove],[data-mv6-upgrade],[data-mv6-checkout],[data-mv6-empty-add],[data-mv6-jump]') : null;
    if (!el || !ROOT.contains(el)) return;
    var s = last || mv6.state();

    if (el.hasAttribute('data-mv6-pick')) {
      var mode = el.getAttribute('data-mv6-pick');
      mv6.setMode(mode);
      /* Tiles are visible in BOTH modes (restored 22 Aug 2026 — this was switch 2
         of the 2 that hid the subscribe tiers; switch 1 was the wrapper gate in
         mv6-buybox.liquid). The selected quantity now RIDES across the mode
         switch deliberately: the tile stays visibly selected, so nothing
         invisible rides into a subscription — the hazard the old reset here
         guarded against. mv6-purchase.js prices the tier through
         `isSub ? q : 1`, unchanged. */
      return;
    }
    if (el.hasAttribute('data-mv6-tile')) { mv6.setQty(parseInt(el.getAttribute('data-mv6-tile'), 10)); return; }

    /* The buy box uses addSelection, NOT addToCart: a one-time line
       accumulates, a subscription line is RE-TIERED (the tier is the
       quantity). Then the drawer opens — we never jump to checkout from here. */
    if (el.hasAttribute('data-mv6-cta')) {
      e.preventDefault();
      mv6.addSelection().then(function () { setCart(true); }).catch(function () {});
      return;
    }
    if (el.hasAttribute('data-mv6-empty-add')) {
      mv6.addLine('sub', 1).catch(function () {});
      return;
    }

    if (el.hasAttribute('data-mv6-cart-open')) { setCart(true); return; }
    if (el.hasAttribute('data-mv6-cart-close') || el.hasAttribute('data-mv6-cart-scrim')) { setCart(false); return; }
    if (el.hasAttribute('data-mv6-menu-toggle')) { menu(!menuOpen); return; }
    if (el.hasAttribute('data-mv6-menu-scrim')) { menu(false); return; }
    if (el.hasAttribute('data-mv6-step')) {
      var code = el.getAttribute('data-mv6-step');
      var kind = code.indexOf('sub') === 0 ? 'sub' : 'ot';
      var line = kind === 'sub' ? s.subLine : s.otLine;
      if (!line) return;
      var delta = code.slice(-1) === '+' ? 1 : -1;
      /* the delta resolves against the cart at EXECUTION time inside mv6's
         queue (R-130); the display holds the tapped value until it settles */
      stepOptimistic(kind, line, code.slice(-1) === '+' ? 1 : -1);
      return;
    }
    if (el.hasAttribute('data-mv6-nudge')) {
      if (el.dataset.act !== '1') return;
      var nk = el.getAttribute('data-mv6-nudge');
      var nl = nk === 'sub' ? s.subLine : s.otLine;
      if (!nl) return;
      stepOptimistic(nk, nl, 1);
      return;
    }
    if (el.hasAttribute('data-mv6-line-remove')) {
      /* removeKind, not removeLine(key): the delete must clear EVERY line of
         its kind — a hidden duplicate surviving a delete is the live
         "deleted three times, count kept counting" report (R-130) */
      sweepSync(true);
      mv6.removeKind(el.getAttribute('data-mv6-line-remove')).catch(function () {});
      return;
    }
    if (el.hasAttribute('data-mv6-upgrade')) { mv6.upgradeToSub().catch(function () {}); return; }
    if (el.hasAttribute('data-mv6-checkout')) { mv6.checkout(); return; }

    /* Every in-page CTA lands on #mv6-title with the sticky header's height
       subtracted, so price, tiles and CTA are all in view — landing on the
       top of the hero SECTION leaves the buy panel under the fold on
       desktop. */
    if (el.hasAttribute('data-mv6-jump')) {
      /* Land on the element the link NAMES (14 Aug 2026, user call): the
         homepage's Save-30% CTAs point at #mv6-buy so the visitor arrives at
         the START OF THE PRODUCT IMAGES — "like opening a PDP" — while the
         PDP's own links keep #mv6-title (price, tiles and CTA in view; landing
         on the hero section top leaves the buy panel under the fold there). */
      var jhash = el.getAttribute('href') || '';
      var target = (jhash.charAt(0) === '#' && document.getElementById(jhash.slice(1))) || document.getElementById('mv6-title');
      if (!target) return;
      e.preventDefault();
      /* document-wide: the nav lives in the mv6-header SECTION since the chrome
         extraction, outside this root. */
      var nav = document.querySelector('[data-mv6-nav]');
      /* #mv6-title keeps the header-height offset (price + CTA in view). Any
         OTHER named target — the homepage's #mv6-buy — lands FLUSH at the top
         of the screen, so the wave/seam above it sits outside the viewport
         and the visitor sees the product image first (user call, 14 Aug 2026
         round 2). The +1 clears the previous section's 1px wave bleed. */
      var pad = target.id === 'mv6-title' ? (nav ? nav.getBoundingClientRect().height : 0) + 12 : -1;
      var y = target.getBoundingClientRect().top + (window.scrollY || 0) - pad;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      if (cartOpen) setCart(false);
      menu(false);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (cartOpen) setCart(false);
    /* Ungated on purpose: menu(false) is idempotent, and gating it on the
       `menuOpen` flag let the flag desync from the DOM under fast repeated
       toggling — the panel could sit visible but inert. Escape now always
       re-asserts the closed state rather than trusting the flag. */
    menu(false);
  });

  /* ── sticky header — owned by assets/mv6-header.js since the chrome extraction;
        this file keeps only the claim-offer bar below ─────────────────────────────────────────── */
  /* REMOVED 14 Aug 2026: the ROOT-scoped navSync that lived here. The nav is
     in sections/mv6-header.liquid since the chrome extraction, so this copy
     matched nothing on the theme — but it sat as a dead SECOND OWNER with
     different thresholds (±4 per frame vs the v14 accumulator in
     mv6-header.js), and it shadowed the real one on any page that embeds a
     nav in-root (the offline harness did). One owner per behavior (R-36). */

  /* ── sticky claim-offer bar ────────────────────────────────────────────
   * Shown once the reader has scrolled PAST THE BUY BOX — not merely once the
   * CTA button leaves the viewport. Those are different tests, and the CTA one
   * was wrong: on a laptop the buy box is taller than the fold, so the button
   * starts out of view and the bar appeared on the very first screen, beside
   * the live CTA it is supposed to stand in for. This matches the design: the
   * bar is a REPLACEMENT for the buy box, so it arrives when the buy box goes. */
  var barOn = false;
  var barOffTimer = 0;

  /* v6.50 (4 Sep 2026, user call) — ONE CHROME BAR AT A TIME. On a phone a
     scroll-up used to bring the header back while the offer bar was still up:
     two fixed bars, ~120px of a 650px screen, over the content the reader came
     back to re-read. Scroll-up is a low-intent moment (re-checking price or a
     review, or heading to the buy box itself); the decision moment is the end
     of a scroll-DOWN, when the header is hidden and the bar is up. So the bar
     follows the header's hide-on-scroll state: header hidden → bar may show;
     header shown → bar retracts. Same accumulator, same thresholds, one owner
     of scroll direction (R-36): mv6-header.js broadcasts `mv6:nav`, this file
     only listens. The header's "Shop now" pill means there is never a frame
     with zero purchase path on screen.
     FAIL-OPEN: until the first `mv6:nav` arrives (header script late, or a
     template without the header section) the bar keeps its pre-v6.50 rule, so
     a missing broadcaster can never delete the bar. */
  var navHidden = false, navSeen = false;
  document.addEventListener('mv6:nav', function (e) {
    var hidden = !!(e.detail && e.detail.hidden);
    navSeen = true;
    if (hidden === navHidden) return;
    navHidden = hidden;
    barSync();
  });

  /* ══ v6.36 — THE BAND WAS THE BAR. SIXTEEN ROUNDS, CLOSED. ═══════════════
     `?mv6t=nobar` on the PDP, scrolled to the footer: the band is GONE. Not a
     compositing artefact, not an app, not a stale iOS tile — the sticky offer
     bar itself, seen correctly.

     Why sixteen rounds missed it. Every elimination reasoned about the bar
     PARKED (translateY(120%), and display:none since v6.34). But the bar shows
     once the buy box scrolls out of view, so by the time you are at the footer
     it is ON: fixed, bottom:0, full width, #FFFFFF, with a 34px upward shadow —
     over a dark footer that reads exactly as "a light band over the statements
     block". The only clean `nobar` reading was round 19's, taken while three
     other layers were still painting, which is why it came back negative and
     shut the door (R-111). Nobody re-ran it for sixteen rounds. The user's own
     intuition — "what if we hide the bottom bar at the footer" — is what
     reopened it.

     Two lessons, and the second is the expensive one. (1) A negative
     discriminator must be re-run every time the field narrows. (2) **We were
     eliminating the wrong STATE of the right element.** "Is element X the cause"
     is not one question when X has two states; the parked state was tested to
     exhaustion and the shown state was never tested at all.

     The fix is also the UX call the user already made months ago, when the bar
     was overlapping footer content and they asked for clearance: a sticky
     purchase CTA has no business sitting over the legal and FDA block. It now
     retracts as the footer arrives and returns when you scroll back up. */
  var footerIn = false;
  if (window.IntersectionObserver) {
    var ftShell = document.getElementById('mv6-footer-shell');
    /* 24px of lead so the bar is already gone before the footer's first row
       reaches it — no overlapping frame on a fast scroll. */
    if (ftShell) new IntersectionObserver(function (es) {
      var now = es[0].isIntersecting;
      if (now === footerIn) return;
      footerIn = now;
      barSync();
    }, { rootMargin: '0px 0px 24px 0px', threshold: 0 }).observe(ftShell);
  }

  function barSync() {
    var bar = $('[data-mv6-bar]');
    if (!bar) return;
    var on = barOn && !cartOpen && !footerIn && (navHidden || !navSeen);
    /* v6.34 — the bar takes the drawer's display cycle. Round 19 exonerated it
       on a discriminator run taken while three other layers were still painting
       a band, which proves nothing (R-111). Dropped from the compositor in the
       MARKUP, un-dropped one frame before the slide-in (with a forced reflow so
       the transition has a start value), re-dropped after the slide-out ends. */
    clearTimeout(barOffTimer);
    if (on) { bar.removeAttribute('data-mv6-off'); void bar.offsetWidth; }
    bar.style.transform = on ? 'translateY(0)' : 'translateY(120%)';
    if (!on) barOffTimer = setTimeout(function () {
      if (barOn && !cartOpen) return;
      bar.setAttribute('data-mv6-off', '');
    }, 460);
    /* R-75 (round 18) — THE BAND. This bar is `position:fixed; bottom:0;
       width:100%` with `rgba(255,255,255,.96)`, `backdrop-filter:blur(14px)` and
       a 34px shadow, parked at translateY(120%) for most of a visit: the nav's
       defect (rounds 8-11) in the one element nobody inventoried, because it
       lives in the PDP section rather than the chrome. iOS composites its stale
       tile as a full-width light band — which is why the band survived the
       header being disabled AND every footer switch. The attribute lets the
       stylesheet drop the layer once the slide-out has finished. */
    if (on) bar.setAttribute('data-mv6-on', ''); else bar.removeAttribute('data-mv6-on');
  }

  /* R-31: the sticky bar tracks the BUY BOX leaving the viewport, not the CTA
     button. #mv6-buy is the buy box, and now the only name for it — the old
     attribute-selector fallback matched nothing in the markup, so it was a dead
     branch that would have hidden a renamed id. dev/check-liquid.js asserts
     that every getElementById target exists in the markup. */
  var heroEl = document.getElementById('mv6-buy');

  /* ⚠ R-89 · NO LAYOUT READS ON THE SCROLL PATH (round 30, 15 Aug 2026).
     This used to call getBoundingClientRect() on the buy box inside the scroll
     rAF — a forced synchronous layout on every frame of every scroll, on BOTH
     pages (the homepage renders this binder too, and #mv6-buy exists there even
     though it has no sticky bar). Together with mv6-home.js's arc read and the
     header's transform write it produced R-71's signature on the scroll path:
     whole-page shimmer / micro-freezes while scrolling on iOS. The buy box's
     bottom is now cached in DOCUMENT coordinates and re-measured only off the
     scroll path — after a render (the tiles mount and unmount), after a tap in
     the section, on load, on a WIDTH-only resize and on three settle timers. */
  var heroBottom = 0;
  function measureHero() {
    if (!heroEl) return;
    heroBottom = heroEl.getBoundingClientRect().bottom + (window.scrollY || 0);
  }
  function heroSync() {
    if (!heroEl) return;
    /* 40px of slack so the bar does not flicker in and out while the reader
       hovers exactly on the boundary. */
    var past = (heroBottom - (window.scrollY || 0)) < -40;
    if (past === barOn) return;
    barOn = past;
    barSync();
  }
  function remeasureHero() { measureHero(); heroSync(); }
  remeasureHero();

  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { heroSync(); ticking = false; });
  }, { passive: true });

  var lastBarW = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastBarW) return;   /* R-71: never on an iOS URL-bar resize */
    lastBarW = window.innerWidth;
    remeasureHero();
  }, { passive: true });
  if (document.readyState !== 'complete') window.addEventListener('load', remeasureHero);
  ROOT.addEventListener('click', function () { requestAnimationFrame(remeasureHero); }, { passive: true });
  setTimeout(remeasureHero, 400);
  setTimeout(remeasureHero, 1400);
  setTimeout(remeasureHero, 2600);

  /* ── boot ─────────────────────────────────────────────────────────────────
   * Synchronous first render: mv6.state() is available the moment the module
   * has parsed the JSON blob, so every price is correct in the first paint —
   * no blank prices and no reflow. The async /cart.js refresh only fills the
   * drawer. */
  /* rAF-coalesced (14 Aug 2026, shake round 3): a sub-pick emits twice (mode
     + the qty-1 reset) and each render is ~60 root-wide queries plus a full
     style recalc from the root attribute flips — two of those synchronously
     inside one tap read as a stutter. Coalescing paints the LAST state once,
     on the next frame. The boot render stays synchronous so first paint is
     priced (R-58). */
  var pendingState = null, renderRaf = 0;
  mv6.on('change', function (s) {
    pendingState = s;
    if (renderRaf) return;
    renderRaf = requestAnimationFrame(function () { renderRaf = 0; render(pendingState); remeasureHero(); });
  });
  render(mv6.state());
})();
